import {
  deArrowRequestUrl,
  err,
  ok,
  parseDeArrowBucket,
  type AbortLike,
  type DeArrowBranding,
  type DeArrowSettings,
  type Result,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { readJsonBody } from './json';
import type { ApiProblem } from './problem';
import { hashPrefix } from './sponsorblock';

/** The community's answer when nothing was looked up or nothing stands — never `undefined`. */
const NOTHING: DeArrowBranding = { title: null, thumbnailTimestampSec: null };

/**
 * The DeArrow branding lookup — the k-anonymity form, always, SponsorBlock's own scheme: the
 * service is sent the first four hex characters of SHA-256(videoId) and answers a ~150-video
 * bucket keyed by id, so which video is on screen never leaves the browser. No WebCrypto (an
 * insecure origin) means the feature is silently absent rather than a louder per-id lookup.
 *
 * Through the shared transport for SponsorBlock's reason too: one retry policy and one error
 * classification for every request the app makes. Unlike `skipSegments`, the
 * bucket route answers `200` even when empty — a `404` is kept as "nothing" anyway, because a
 * self-hosted server that speaks it should not paint a grid's worth of errors.
 */
export async function fetchDeArrowBranding(
  platform: Platform,
  settings: DeArrowSettings,
  videoId: string,
  signal?: AbortLike,
): Promise<Result<DeArrowBranding, ApiProblem>> {
  const prefix = await hashPrefix(videoId);
  if (prefix === null) return ok(NOTHING);

  const url = deArrowRequestUrl(settings, prefix);
  if (url === null) return ok(NOTHING);

  const response = await platform.transport.send({ method: 'GET', url, headers: {}, signal });
  if (!response.ok) {
    if (response.error.status === 404) return ok(NOTHING);
    return err({ kind: 'transport', error: response.error });
  }

  const body = readJsonBody(response.value.body);
  if (!body.ok) return err(body.error);
  return ok(parseDeArrowBucket(body.value, videoId));
}
